import React from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { 
  CheckCircleIcon,
  StarIcon,
  ArrowUpIcon,
  ShieldCheckIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const OrganizationSubscription = ({ organization, subscriptionData }) => {
  // Use passed subscription data if available, otherwise fall back to context
  const {
    subscriptionStatus: contextSubscriptionStatus,
    pricingPlans,
    statusLoading,
    plansLoading,
    upgradePlan,
    isUpgrading
  } = useSubscription();

  // Use passed data if available, otherwise use context data
  const subscriptionStatus = subscriptionData || contextSubscriptionStatus;
  const isLoading = statusLoading || plansLoading;

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-xl shadow-2xl p-8 border border-gray-700">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-700 rounded-lg"></div>
            <div className="h-64 bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus) {
    return (
      <div className="bg-gray-800 rounded-xl shadow-2xl p-8 border border-gray-700">
        <div className="text-center py-8">
          <ShieldCheckIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-300 mb-2">Subscription Status Unavailable</h3>
          <p className="text-gray-400">Unable to load subscription information.</p>
        </div>
      </div>
    );
  }

  const currentPlan = subscriptionStatus.current_plan;
  
  // Determine plan type and free status from the actual data
  const isFree = currentPlan?.plan_type === 'free';

  const handleUpgrade = (planId) => {
    if (window.confirm('Are you sure you want to upgrade to this plan?')) {
      upgradePlan(planId);
    }
  };

  return (
    <div className="space-y-8">
      {/* Current Plan Status */}
      <div className="bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-700/50 backdrop-blur-sm">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-white">Current Plan</h2>
            <p className="text-gray-300 text-sm">Your organization's subscription status and features</p>
          </div>
        </div>

        {currentPlan && (
          <div className="space-y-8">
            {/* Plan Type and Due Date - Connected Layout */}
            <div className="bg-gradient-to-br from-gray-700/40 via-gray-700/20 to-gray-800/40 rounded-2xl p-6 border border-gray-600/20">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-xl border border-blue-500/30 shadow-lg">
                      <StarIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-medium mb-1">Plan Type</p>
                      <p className="text-2xl font-bold text-white">{currentPlan.name}</p>
                    </div>
                  </div>
                </div>

                {subscriptionStatus.next_billing_date && (
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-gradient-to-br from-emerald-500/30 to-emerald-600/30 rounded-xl border border-emerald-500/30 shadow-lg">
                        <CalendarIcon className="h-6 w-6 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm font-medium mb-1">Next Due Date</p>
                        <p className="text-2xl font-bold text-emerald-400">
                          {new Date(subscriptionStatus.next_billing_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status - Connected to the above section */}
            <div className="bg-gradient-to-br from-gray-700/40 via-gray-700/20 to-gray-800/40 rounded-2xl p-6 border border-gray-600/20">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500/30 to-emerald-600/30 rounded-xl border border-emerald-500/30 shadow-lg">
                  <ShieldCheckIcon className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm font-medium mb-1">Subscription Status</p>
                  <p className="text-2xl font-bold text-emerald-400">Active</p>
                </div>
              </div>
            </div>

            {/* Amount - Hero Section at Bottom */}
            <div className="bg-gradient-to-br from-blue-500/10 via-blue-600/5 to-blue-700/10 rounded-2xl p-8 border border-blue-500/20 text-center">
              <p className="text-gray-300 text-sm font-medium mb-3">Subscription Amount</p>
              <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent mb-4">
                ₹{currentPlan.price}
                <span className="text-2xl text-gray-400 ml-3 font-normal">
                  {currentPlan.plan_type === 'free' ? 'forever' : 
                   currentPlan.plan_type === 'monthly' ? '/month' : '/year'}
                </span>
              </div>
              <p className="text-gray-300 text-base">{currentPlan.description}</p>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Section */}
      {isFree && pricingPlans && pricingPlans.length > 1 && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-700">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
              <ArrowUpIcon className="h-8 w-8 text-blue-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Upgrade Your Plan</h2>
            <p className="text-gray-400 text-lg">Get unlimited access to all features and scale your organization</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingPlans.filter(plan => plan.plan_type !== 'free').map((plan) => (
              <div key={plan.plan_id} className={`bg-gray-700/50 backdrop-blur-sm rounded-xl p-8 border-2 ${
                plan.is_popular ? 'border-blue-500 shadow-blue-500/20' : 'border-gray-600'
              } relative hover:scale-105 transition-all duration-300`}>
                {plan.is_popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-3">{plan.name}</h3>
                  <p className="text-gray-400 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-blue-400">₹{plan.price}</span>
                    <span className="text-gray-400 ml-3 text-lg">{plan.duration}</span>
                  </div>

                  {plan.savings && (
                    <div className="inline-block bg-emerald-600 text-white text-sm px-4 py-2 rounded-full mb-6 font-medium">
                      {plan.savings}
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.slice(0, 6).map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 6 && (
                    <li className="text-sm text-gray-400 text-center pt-2">
                      +{plan.features.length - 6} more features
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.plan_id)}
                  disabled={isUpgrading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl disabled:opacity-50"
                >
                  {isUpgrading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Upgrading...</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpIcon className="h-5 w-5" />
                      <span>Upgrade Now</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSubscription; 